import { LayoutChangeEvent, Pressable, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useTheme } from "../../hooks/use-theme";
import Icon, { IconName } from "../../components/Icon";
import { useDropdown } from "../../hooks/use-dropdown";
import Text from "../../components/Text";
import { ScreenHeaderManagerContext } from "../../contexts/ScreenHeaderManager";
import Button from "../../components/Button";
import { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";

const BUTTON_ANIMATION_DURATION = 300;

export type MenuMode = "actionSheet" | "menu";

export type HeaderButtonProps = {
    label: string;
    disabled?: boolean;
    onPress: () => void;
    style?: StyleProp<ViewStyle>;
};

export const HeaderButton: React.FC<HeaderButtonProps> = ({ label, onPress, disabled, style }) => {
    const { colors, fonts } = useTheme();

    return (
        <Pressable onPress={onPress} style={style}>
            <Text
                style={{
                    ...fonts.bodyMedium,
                    color: disabled ? colors.onSurfaceDisabled : colors.secondary,
                }}
            >
                {label}
            </Text>
        </Pressable>
    );
};

export type MenuAction = {
    label: string;
    icon: IconName;
    color?: string;
    style?: StyleProp<ViewStyle>;
    onPress: () => void;
    testID?: string;
};

type screenParams = StackScreenProps<RootStackParamList>;

type ActionButtonProps = {
    icon: IconName;
    onPress: () => void;
    bordered?: boolean;
    testID?: string;
    ref?: React.RefObject<View>;
    disabled?: boolean;
};

// eslint-disable-next-line react/display-name
export const ActionButton = forwardRef<View, ActionButtonProps>(
    ({ icon, onPress, bordered, testID, disabled }: ActionButtonProps, ref) => {
        const { colors, gridSize } = useTheme();
        const styles = useStyles();
        return (
            // collapsable must be set to false to be able to use measureInWindow ( see use-dropdown )
            // see: https://github.com/facebook/react-native/issues/29712
            <View ref={ref} style={{ opacity: 1 }} collapsable={false}>
                <Pressable
                    onPress={disabled ? undefined : onPress}
                    testID={testID}
                    id={testID}
                    disabled={disabled}
                    style={[
                        {
                            alignItems: "center",
                            justifyContent: "center",
                            flex: 1,
                            flexGrow: 10,
                            flexShrink: 0,
                            width: gridSize * 2,
                        },
                    ]}
                >
                    <Icon
                        name={icon}
                        size={24}
                        style={[
                            { height: 24, width: 24, flexDirection: "row", alignItems: "center" },
                            bordered ? styles.headerButtonBordered : {},
                        ]}
                        color={disabled ? colors.onSurfaceDisabled : colors.primary}
                    />
                </Pressable>
            </View>
        );
    },
);

export type ScreenHeaderProps = {
    label?: string;
    component?: React.ReactNode;
    subLabel?: string;
    actions?: MenuAction[];
    leftIcon?: IconName;
    onLeftAction?: "back" | (() => void);
    leftActionDisabled?: boolean;
    hideProfile?: boolean;
    menuMode?: MenuMode;
    useSmallLabel?: boolean;
    navigation?: screenParams["navigation"];
    useHeaderButton?: boolean;
    id?: string;
};

const ScreenHeader: React.FC<ScreenHeaderProps> = ({
    label,
    component,
    subLabel,
    actions = [],
    onLeftAction,
    leftIcon,
    leftActionDisabled,
    useSmallLabel = false,
    navigation,
    useHeaderButton = false,
    id,
}) => {
    const styles = useStyles();
    const { dynamicButtonConfig, isScreenScrolled } = useContext(ScreenHeaderManagerContext);
    const navigationGlobal = useNavigation<screenParams["navigation"]>();
    const isLarge = useIsScreenSizeMin("large");

    if (!navigation) {
        navigation = navigationGlobal;
    }

    const [headerHeight, setHeaderHeight] = useState(0);

    // Values when isScreenScrolled = [false, true]
    const animationButtonTop = useMemo(() => [headerHeight, 0], [headerHeight]);

    const top = useSharedValue(animationButtonTop[+isScreenScrolled]);

    useEffect(() => {
        top.value = withTiming(animationButtonTop[+isScreenScrolled], {
            duration: BUTTON_ANIMATION_DURATION,
        });
    }, [animationButtonTop, isScreenScrolled, top]);

    const animatedButtonStyles = useAnimatedStyle(() => ({
        top: top.value,
    }));

    const { colors } = useTheme();
    const dropDownItems = useMemo(
        () =>
            actions.map((action) => ({
                label: action.label,
                icon: action.icon,
                color: action.color,
                style: action.style,
                onClick: action.onPress,
                testID: action.testID,
            })),
        [actions],
    );
    const dropdownProps = useDropdown({
        items: dropDownItems,
    });
    const labelAlignStyle = onLeftAction ? styles.withLeftAction : styles.withoutLeftAction;

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setHeaderHeight(e.nativeEvent.layout.height);
    }, []);

    return (
        <View style={styles.container} onLayout={onLayout}>
            {onLeftAction ? (
                <Pressable
                    id={id}
                    style={[styles.leftAction]}
                    onPress={
                        !leftActionDisabled
                            ? onLeftAction === "back"
                                ? navigation.goBack
                                : onLeftAction
                            : null
                    }
                >
                    {leftIcon ? (
                        <Icon
                            name={leftIcon}
                            style={{
                                marginTop: 2,
                                alignSelf: subLabel ? "flex-start" : undefined,
                            }}
                            color={leftActionDisabled ? colors.onSurfaceDisabled : colors.primary}
                            size={28}
                        />
                    ) : (
                        <Icon
                            name="back"
                            style={{ marginTop: 2, alignSelf: subLabel ? "flex-start" : undefined }}
                            size={28}
                            color={leftActionDisabled ? colors.onSurfaceDisabled : colors.primary}
                        />
                    )}
                </Pressable>
            ) : null}
            {label &&
                (subLabel ? (
                    <View style={[styles.labelView, labelAlignStyle]}>
                        <Text style={styles.labelSmallText} numberOfLines={1}>
                            {label}
                        </Text>
                        <Text style={styles.subLabelText} numberOfLines={1}>
                            {subLabel}
                        </Text>
                    </View>
                ) : (
                    <View style={[styles.labelView, labelAlignStyle]}>
                        <Text
                            style={
                                useSmallLabel
                                    ? styles.labelSmallText
                                    : [styles.labelLargeText, isLarge && { lineHeight: 70 }]
                            }
                            numberOfLines={2}
                        >
                            {label}
                        </Text>
                    </View>
                ))}
            {component && <View style={[styles.componentView]}>{component}</View>}
            {dynamicButtonConfig && useHeaderButton && (
                <Button
                    size="small"
                    leftIcon="add"
                    label={dynamicButtonConfig.shortTitle || dynamicButtonConfig.title}
                    onPress={dynamicButtonConfig.onClick}
                    buttonMode="light"
                    style={[animatedButtonStyles, { alignSelf: "center" }]}
                />
            )}
            <View style={[styles.actions, subLabel ? { height: 35 } : { marginTop: 5 }]}>
                {actions.length === 1 ? (
                    <ActionButton
                        icon={actions[0].icon}
                        onPress={actions[0].onPress}
                        testID={actions[0].testID}
                    />
                ) : actions.length > 1 ? (
                    <ActionButton
                        {...dropdownProps}
                        icon="menu"
                        testID="user-header-dropdown-menu"
                    />
                ) : null}
            </View>
        </View>
    );
};

const useStyles = () => {
    const { colors, gridSize, marginSize, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingHorizontal: marginSize,
                    width: "100%",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    overflow: "hidden",
                },
                labelView: {
                    flexGrow: 1,
                    flexShrink: 1,
                    alignSelf: "center",
                    lineHeight: 30,
                },
                componentView: {
                    flexGrow: 1,
                    flexShrink: 1,
                    alignSelf: "flex-start",
                },
                withLeftAction: { alignItems: "center" },
                withoutLeftAction: { alignItems: "flex-start" },
                labelSmallText: {
                    ...fonts.titleMedium,
                    lineHeight: 30,
                },
                labelLargeText: {
                    ...fonts.titleLarge,
                },
                subLabelText: {
                    ...fonts.bodySmall,
                    color: colors.onSurfaceDisabled,
                    lineHeight: 17,
                },
                headerButtonBordered: {
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: colors.primary,
                },
                clientIconBorder: {
                    height: 24,
                    width: 24,
                    flexDirection: "row",
                    alignItems: "flex-end",
                    justifyContent: "center",
                },
                actions: {
                    width: gridSize,
                    height: gridSize * 2,
                    paddingBottom: 4,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
                screenTitle: {
                    fontSize: 30,
                    fontWeight: "bold",
                },
                leftAction: {
                    flexGrow: 0,
                    width: gridSize,
                    height: gridSize * 2,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
            }),
        [
            colors.onSurfaceDisabled,
            colors.primary,
            fonts.bodySmall,
            fonts.titleLarge,
            fonts.titleMedium,
            gridSize,
            marginSize,
        ],
    );
};

export default ScreenHeader;
